import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { syncPropertyNames } from '@theme/roots-addon-properties';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Pairs a parent product with a hidden add-on service product (e.g. blouse
 * stitching). When the service is enabled, the buy form submit is taken over
 * and both the parent product (carrying the measurements as line item
 * properties) and the service product are added to the cart in a single
 * atomic `/cart/add` request — Shopify rejects the whole request if any item
 * fails, so the order can never end up with measurements but no charge or
 * vice versa.
 *
 * Lines are linked through hidden properties: the parent line gets
 * `_addon_ref` and each service line gets `_addon_parent_ref` with the same
 * value, plus `_addon_count` with the number of service lines added.
 * `assets/roots-addon-cart-guard.js` uses this pairing to keep quantities in
 * sync and to remove any half of a pair whose other half is gone.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} toggle - The service opt-in checkbox.
 * @property {HTMLElement} panel - The collapsible options panel.
 * @property {HTMLElement} errorMessage - The error live region.
 * @extends {Component<Refs>}
 */
class RootsAddonService extends Component {
  // No 'panel': services with nothing to collect (petticoat, fall & pico)
  // render the toggle row only.
  requiredRefs = ['toggle', 'errorMessage'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    // WINDOW capture, deliberately: Horizon's component.js delegates
    // `on:submit` through a document-level capture listener registered at
    // page load, which would always run before anything we attach to the
    // document or the form. The capture phase starts at window, so this is
    // the one hook guaranteed to run first — independent of registration
    // order, module timing, or the form node being replaced by morphs.
    window.addEventListener('submit', this.#handleSubmit, { capture: true });

    // A restored form state (back/forward navigation) could re-check two
    // exclusive services; keep the first one on.
    if (this.active && this.#hasActiveExclusiveSibling()) this.refs.toggle.checked = false;
    this.#syncState();
  }

  #hasActiveExclusiveSibling() {
    const group = this.dataset.exclusiveGroup;
    if (!group) return false;

    return Array.from(document.querySelectorAll('roots-addon-service')).some(
      (sibling) =>
        sibling !== this &&
        // Query the switch directly: an earlier sibling's refs may not be
        // initialised yet while elements upgrade.
        /** @type {HTMLInputElement | null} */ (sibling.querySelector('[ref="toggle"]'))?.checked === true &&
        sibling.dataset.exclusiveGroup === group &&
        sibling.dataset.productFormId === this.dataset.productFormId &&
        sibling.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('submit', this.#handleSubmit, { capture: true });
  }

  get active() {
    return Boolean(this.refs.toggle.checked);
  }

  /**
   * The service variant to add: the customer's pick when the service product
   * has multiple variants (e.g. padded/unpadded), otherwise the default.
   */
  get selectedVariantId() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-addon-variant-option]:checked')
    );
    return checked?.value || this.dataset.addonVariantId || '';
  }

  toggleChanged() {
    if (this.active) this.#deactivateExclusiveSiblings();
    this.#syncState();
  }

  /**
   * Services sharing `data-exclusive-group` on the same product form can't be
   * on together: the blouse services submit identically named properties
   * (Blouse fit, Bust (in), Blouse front design...), so one would overwrite
   * the other on the order. Turning one on switches the others off.
   */
  #deactivateExclusiveSiblings() {
    const group = this.dataset.exclusiveGroup;
    if (!group) return;

    for (const sibling of /** @type {NodeListOf<RootsAddonService>} */ (
      document.querySelectorAll('roots-addon-service')
    )) {
      if (
        sibling !== this &&
        sibling instanceof RootsAddonService &&
        sibling.active &&
        sibling.dataset.exclusiveGroup === group &&
        sibling.dataset.productFormId === this.dataset.productFormId
      ) {
        sibling.refs.toggle.checked = false;
        sibling.#syncState();
      }
    }
  }

  /**
   * Reflects the picked variant's price in the row header.
   *
   * @param {Event} event
   */
  variantChanged(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const priceDisplay = /** @type {HTMLElement | undefined} */ (this.refs.priceDisplay);
    if (input?.dataset.price && priceDisplay) {
      priceDisplay.textContent = `+ ${input.dataset.price}`;
    }
  }

  get #fields() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-addon-field]'));
  }

  #syncState() {
    const active = this.active;
    const panel = /** @type {HTMLElement | undefined} */ (this.refs.panel);

    this.classList.toggle('roots-addon-service--open', active);
    panel?.toggleAttribute('inert', !active);
    if (panel) this.refs.toggle.setAttribute('aria-expanded', String(active));

    for (const field of this.#fields) {
      field.disabled = !active;
      if ('addonRequired' in field.dataset) field.required = active;
    }

    // Nested fit and design modules manage their own fields (only the
    // visible mode is enabled and required); they read the panel's inert
    // state, so they must re-sync after it changes.
    for (const module of this.querySelectorAll('roots-blouse-fit, roots-blouse-design')) {
      if (typeof (/** @type {any} */ (module)).sync === 'function') /** @type {any} */ (module).sync();
    }

    // Only enabled fields carry a property name, so nothing that serializes
    // the product form (Buy it now included) can pick up an add-on the
    // customer didn't switch on.
    syncPropertyNames(this);

    // Buy it now checks out the product alone, without the paired service
    // line, so it can't be offered while a service is on.
    this.#syncAcceleratedCheckout();

    if (!active) this.#hideError();
  }

  /**
   * Hides the accelerated checkout buttons whenever any add-on on this
   * product form is active. A CSS `:has()` rule used to do this, but it is
   * silently ignored by older mobile browsers, so drive it from a class.
   */
  #syncAcceleratedCheckout() {
    const details = this.closest('.product-details');
    if (!details) return;

    const formId = this.dataset.productFormId;
    const anyActive = Array.from(document.querySelectorAll('roots-addon-service')).some(
      (addon) => addon instanceof RootsAddonService && addon.dataset.productFormId === formId && addon.active
    );

    details.classList.toggle('roots-hide-accelerated-checkout', anyActive);
  }

  /**
   * Every instance bound to the form runs this, but the first one to fire
   * calls `stopImmediatePropagation`, so exactly one instance handles the
   * submit on behalf of all active add-ons on the form.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('id') !== this.dataset.productFormId) return;

    const addons = RootsAddonService.#activeAddonsFor(form);

    // No active add-on: let Horizon's native single-item flow proceed.
    if (addons.length === 0 || event.defaultPrevented) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#addToCart(form, addons);
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {RootsAddonService[]}
   */
  static #activeAddonsFor(form) {
    // getAttribute, NOT form.id: the form contains <input name="id"> (the
    // variant input), and named controls shadow the form's DOM properties —
    // form.id resolves to that input element instead of the id string.
    const formId = form.getAttribute('id');
    return /** @type {RootsAddonService[]} */ (
      Array.from(document.querySelectorAll('roots-addon-service'))
    ).filter((addon) => addon.dataset.productFormId === formId && addon.active);
  }

  /**
   * Adds the parent product and all active service products in one atomic
   * request, then resolves the same events Horizon's product form dispatches
   * so the cart drawer, cart icon, and cart items update natively.
   *
   * @param {HTMLFormElement} form
   * @param {RootsAddonService[]} addons
   */
  async #addToCart(form, addons) {
    if (this.#busy) return;
    this.#busy = true;
    this.#hideError();

    const formData = new FormData(form);
    const variantId = String(formData.get('id') ?? '');
    const quantity = Number(formData.get('quantity')) || 1;
    const addonRef = RootsAddonService.#generateRef();

    /** @type {Record<string, string>} */
    // `_addon_count` lets the cart guard spot a parent whose service lines
    // were removed (see roots-addon-cart-guard.js).
    const parentProperties = { _addon_ref: addonRef, _addon_count: String(addons.length) };
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match?.[1] && typeof value === 'string' && value.trim() !== '') {
        parentProperties[match[1]] = value.trim();
      }
    }

    const items = [
      { id: Number(variantId), quantity, properties: parentProperties },
      ...addons.map((addon) => ({
        id: Number(addon.selectedVariantId),
        quantity,
        properties: {
          For: addon.dataset.parentTitle ?? '',
          _addon_parent_ref: addonRef,
        },
      })),
    ];

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        // Shopify rejected the request; multi-item adds are atomic, so
        // nothing was added.
        this.#showError(data.description || data.message || GENERIC_ERROR_MESSAGE);
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );

        const ajaxCart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'roots-addon-service',
            itemCount: 0,
            productId: this.dataset.parentProductId,
          },
        });
        return;
      }

      const ajaxCart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          itemCount: quantity,
          productId: this.dataset.parentProductId,
          source: 'roots-addon-service',
          sections: data.sections,
        },
      });
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      this.#showError(GENERIC_ERROR_MESSAGE);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Mirrors product-form's cart refresh so resolved events carry the full
   * post-add cart state.
   */
  async #fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  static #generateRef() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().slice(0, 8);
    }
    return Math.random().toString(36).slice(2, 10);
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.errorMessage.textContent = message;
    this.refs.errorMessage.hidden = false;
  }

  #hideError() {
    this.refs.errorMessage.hidden = true;
    this.refs.errorMessage.textContent = '';
  }
}

if (!customElements.get('roots-addon-service')) {
  customElements.define('roots-addon-service', RootsAddonService);
}
